import styled, { keyframes } from 'styled-components';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import imageHelper from '@utils/imageHelper';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import H4 from '@base/H4/H4';
import H6 from '@base/H6/H6';
import Divider from '@base/Divider/Divider';
import Button from '@base/Button/Button';
import analyticsAction from '@actions/analytics';
import Play from '@icon/Play';
import { Spacings, BorderColors, BgColors } from '@styles/index';

const Wrapper = styled.div`
  background: ${BgColors.WHITE};
  width: 100%;
  box-shadow: ${({ theme }) => boxShadowGenerator({ theme })};
  border-top-width: ${Spacings.SPACING_2B};
  border-top-color: ${BorderColors.LIGHTBLUE};
  border-top-style: solid;
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_6B} ${Spacings.SPACING_8B};
  margin-bottom: ${Spacings.SPACING_4B};
  display: flex;
  flex-direction: column;
`;

const TestName = styled(H4)`
  @media only screen and (max-width: 540px) {
    text-align: left;
  }
`;

const Date = styled.p`
  margin-top: 8px;
  margin-bottom: 24px;
  color: ${({ liveTag }) => (liveTag ? '#EB5757' : '#3c4852')};
  padding-right: ${({ liveTag }) => liveTag && '4px'};
  font-weight: ${({ liveTag }) => liveTag && '600'};
  @media only screen and (max-width: 540px) {
    text-align: left;
  }
`;

const PulsatingDot = keyframes`
  0% { box-shadow: ${boxShadowGenerator({
    yOffset: 0,
    blur: 0,
    color: 'rgba(235,87,87, 0.4)'
  })}; }
  70% { box-shadow: ${boxShadowGenerator({
    yOffset: 0,
    blur: 0,
    spread: '10px',
    color: 'rgba(235,87,87, 0)'
  })}; }
  100% {
    box-shadow: none;
  }
`;

export const Live = styled.div`
  margin-right: 12px;
  margin-top: 16px;
  border-radius: 50%;
  box-shadow: ${boxShadowGenerator({
    yOffset: 0,
    blur: 0,
    color: 'rgba(235,87,87, 0.4)'
  })};
  animation: ${PulsatingDot} 2s infinite;
  :hover {
    animation: none;
  }
  height: 4px;
  width: 4px;
  background-color: #eb5757;
`;

const KeyPoints = styled.div`
  margin-top: 24px;
  margin-bottom: 32px;
`;

const Point = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  img {
    height: 32px;
    width: 32px;
    margin-right: 16px;
    @media only screen and (max-width: 540px) {
      height: 24px;
      width: 24px;
    }
  }
  h6 {
    @media only screen and (max-width: 540px) {
      font-size: 12px;
      font-weight: normal;
      text-align: left;
    }
  }
`;

const ProdigyTestCard = ({ data, onClickEnroll, isEnrolled }) => {
  const dispatch = useDispatch();
  const handleOnClickEnroll = () => {
    if (onClickEnroll) {
      onClickEnroll(!data.isEnrolled, data.uid, data);
    }
    dispatch(analyticsAction('courseEnroll', data.uid, data, {}));
  };

  const beginTest = () => {
    window.open(data.permalink);
  };

  return (
    <Wrapper>
      <TestName>{data.name}</TestName>
      {data.isLive ? (
        <div style={{ display: 'flex' }}>
          <Live />
          <Date liveTag>Live</Date>
          <Date>
            {`  •  ${data.isGoingToBeLive ? 'Starts' : 'Started'} at ${
              data.startTime
            }`}
          </Date>
        </div>
      ) : (
        <Date>{data.date}</Date>
      )}
      <Divider />
      <KeyPoints>
        <Point>
          <img src={imageHelper.getImage('prodigy/clock-mono.svg')} alt="" />
          <H6>{data.duration}</H6>
        </Point>
        <Point>
          <img src={imageHelper.getImage('prodigy/book-mono.svg')} alt="" />
          <H6>{data.topics}</H6>
        </Point>
        <Point>
          <img src={imageHelper.getImage('prodigy/question-mono.svg')} alt="" />
          {data.totalCount ? (
            <H6>{`Total of ${data.totalCount} questions`}</H6>
          ) : (
            <H6>{`${data.questionCount} questions per section`}</H6>
          )}
        </Point>
        <Point>
          <img src={imageHelper.getImage('prodigy/negative-mono.svg')} alt="" />
          <H6>
            {data.negativeMarks
              ? 'Negative marks for incorrect answers'
              : 'No negative marking'}
          </H6>
        </Point>
      </KeyPoints>
      <Button
        label={
          // eslint-disable-next-line no-nested-ternary
          data.isQuizAvailable
            ? 'Begin test'
            : isEnrolled
            ? 'Enrolled'
            : 'Enroll'
        }
        variant={data.isQuizAvailable || !isEnrolled ? 'filled' : 'hollow'}
        theme={data.isQuizAvailable || !isEnrolled ? 'green' : 'black'}
        icon={data.isQuizAvailable ? <Play color="white" /> : null}
        grid={3}
        onClick={data.isQuizAvailable ? beginTest : handleOnClickEnroll}
      />
    </Wrapper>
  );
};

export default ProdigyTestCard;

ProdigyTestCard.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  onClickEnroll: PropTypes.func.isRequired,
  isEnrolled: PropTypes.bool
};

ProdigyTestCard.defaultProps = {
  isEnrolled: false
};
